// Path: src/components/aols/aeo/OLSupdates/OLS2900/OLS2945.tsx
// Fixed JSX syntax by adding missing closing </div> tags in widget preview sections
import React from "react";
import { Layout, Typography, Breadcrumb } from "antd";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { HomeOutlined } from "@ant-design/icons";
import { InlineShareButtons } from "sharethis-reactjs";

const { Title, Paragraph } = Typography;

const UpdateContainer = styled.div`
  background: #000000;
  min-height: 100vh;
  padding: 40px 20px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const UpdateContent = styled.div`
  max-width: 800px;
  width: 100%;
  background: #1a1a1a;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  text-align: center;
`;

const UpdateTitle = styled(Title)`
  && {
    color: #ffcc00 !important;
    margin-bottom: 20px;
  }
`;

const UpdateParagraph = styled(Paragraph)`
  && {
    color: #fff !important;
    font-size: 16px;
    line-height: 1.8;
    margin-bottom: 24px;
  }
`;

const UpdateLink = styled.a`
  color: #ffaa00 !important;
  text-decoration: none;
  font-size: 16px;
  transition: color 0.3s ease;

  &:hover {
    color: #ffcc00 !important;
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid #40c4ff;
    outline-offset: 2px;
  }
`;

const HighlightedText = styled.span`
  font-size: 48px;
  color: #ffcc00;
`;

const UpdateImage = styled.img`
  max-width: 100%;
  border-radius: 8px;
  margin: 20px 0;
`;

const WidgetCodeWrapper = styled.pre`
  background: #1a1a1a;
  color: #ffffff;
  padding: 15px;
  border-radius: 8px;
  overflow-x: auto;
  font-family: 'Courier New', Courier, monospace;
  font-size: 14px;
  margin-top: 20px;
  border: 1px solid #ffcc00;
  text-align: left;
`;

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #ffcc00;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #ffaa00;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const ShareThisWrapper = styled.div`
  margin-top: 20px;
  padding: 10px;
  background: #1a1a1a;
  border-radius: 8px;
  display: flex;
  justify-content: center;

  .st-btn {
    border: 1px solid #ffcc00 !important;
    border-radius: 4px !important;
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.05);
    }

    &:focus {
      outline: 2px solid #40c4ff;
      outline-offset: 2px;
    }
  }

  .st-total {
    color: #fff !important;
    font-size: 16px;
  }
`;

const OLS2945: React.FC = () => {
  const calendarWidgetCode = `
<div id="ols-calendar-widget"></div>
<link rel="stylesheet" href="https://www.olsme.com/widgets/ols-calendar-widget.css">
<script src="https://www.olsme.com/widgets/ols-calendar-widget.js"></script>
  `.trim();

  const lightTimeClockWidgetCode = `
<div id="ols-light-time-clock"></div>
<link rel="stylesheet" href="https://www.olsme.com/widgets/ols-light-time-clock.css">
<script src="https://www.olsme.com/widgets/ols-light-time-clock.js"></script>
  `.trim();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: "Limited-Time Offer: Add OLS Calendar Widget & New Light Time Clock to Your Website!",
    description:
      "For a limited time (May 17–31, 2025), add the OLS Calendar Widget to your website and show support for the correct calendar in use. Introducing our new Light Time Clock Widget for Sun-synchronized timekeeping. Learn more at onelightsystem OLS!",
    url: "https://www.olsme.com/olsupdates/ols2900/2945",
    datePublished: "2025-05-17",
    author: {
      "@type": "Organization",
      name: "onelightsystem OLS",
      url: "https://www.olsme.com",
    },
    publisher: {
      "@type": "Organization",
      name: "onelightsystem OLS",
      url: "https://www.olsme.com",
      logo: "https://www.olsme.com/logo.jpg",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://www.olsme.com/olsupdates/ols2900/2945",
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.olsme.com",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "OLS Updates",
          item: "https://www.olsme.com/olsupdates",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "OLS2900",
          item: "https://www.olsme.com/olsupdates/ols2900",
        },
        {
          "@type": "ListItem",
          position: 4,
          name: "Limited-Time Offer: Add OLS Calendar Widget & New Light Time Clock",
          item: "https://www.olsme.com/olsupdates/ols2900/2945",
        },
      ],
    },
  };

  return (
    <Layout>
      <Helmet>
        <title>Limited-Time Offer: Add OLS Calendar Widget & New Light Time Clock to Your Website! - onelightsystem OLS</title>
        <meta
          name="description"
          content="For a limited time (May 17–31, 2025), add the OLS Calendar Widget to your website and show support for the correct calendar in use. Introducing our new Light Time Clock Widget for Sun-synchronized timekeeping. Learn more at onelightsystem OLS!"
        />
        <meta
          name="keywords"
          content="OLS Calendar Widget, Light Time Clock Widget, Sun Light Meditation, correct calendar, onelightsystem OLS, Sun-synchronized time, limited-time offer, OLS updates, Light Civilization, meditation education"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="onelightsystem OLS" />
        <link
          rel="canonical"
          href="https://www.olsme.com/olsupdates/ols2900/2945"
        />
        <meta
          property="og:title"
          content="Limited-Time Offer: Add OLS Calendar Widget & New Light Time Clock to Your Website! - onelightsystem OLS"
        />
        <meta
          property="og:description"
          content="For a limited time (May 17–31, 2025), add the OLS Calendar Widget to your website and show support for the correct calendar in use. Introducing our new Light Time Clock Widget for Sun-synchronized timekeeping. Learn more at onelightsystem OLS!"
        />
        <meta
          property="og:url"
          content="https://www.olsme.com/olsupdates/ols2900/2945"
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:image"
          content="https://via.placeholder.com/1200x630?text=OLS+Widgets+Announcement"
        />
        <meta
          property="og:image:alt"
          content="OLS Calendar Widget and Light Time Clock Announcement"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="onelightsystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta
          name="twitter:title"
          content="Limited-Time Offer: Add OLS Calendar Widget & New Light Time Clock to Your Website! - onelightsystem OLS"
        />
        <meta
          name="twitter:description"
          content="For a limited time (May 17–31, 2025), add the OLS Calendar Widget to your website and show support for the correct calendar in use. Introducing our new Light Time Clock Widget for Sun-synchronized timekeeping. Learn more at onelightsystem OLS!"
        />
        <meta
          name="twitter:image"
          content="https://via.placeholder.com/1200x630?text=OLS+Widgets+Announcement"
        />
        <meta
          name="twitter:image:alt"
          content="OLS Calendar Widget and Light Time Clock Announcement"
        />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <BreadcrumbWrapper>
        <Breadcrumb
          items={[
            {
              title: (
                <Link to="/">
                  <HomeOutlined /> Home
                </Link>
              ),
            },
            {
              title: <Link to="/olsupdates">OLS Updates</Link>,
            },
            {
              title: <Link to="/olsupdates/ols2900">OLS2900</Link>,
            },
            {
              title: "Limited-Time Offer: Add OLS Calendar Widget & New Light Time Clock",
            },
          ]}
        />
      </BreadcrumbWrapper>

      <UpdateContainer>
        <UpdateContent>
          <UpdateTitle level={1}>Limited-Time Offer: Add OLS Calendar Widget & New Light Time Clock to Your Website!</UpdateTitle>
          <UpdateParagraph>
            We’re thrilled to announce a radiant opportunity for a limited time! From today, <HighlightedText>May 17</HighlightedText> to <HighlightedText>May 31, 2025</HighlightedText>, you can add the OLS Calendar Widget to your website and show your support for the <strong>correct calendar in use</strong>—aligned with Sun cycles, starting December 22. This is a welcoming chance to join onelightsystem OLS in fostering a global Sun Light Civilization through accurate timekeeping and meditation practices.
          </UpdateParagraph>
          <UpdateParagraph>
            <strong>Exciting News!</strong> We’re also introducing our new <HighlightedText>Light Time Clock Widget</HighlightedText>, a beautiful addition that displays a Sun-synchronized clock to help you align your daily rhythms with natural light cycles. Both widgets are free for personal use, linking back to olsme.com as part of our exchange benefits agreement. For commercial use, please contact us at <UpdateLink href="https://www.olsme.com/contact" target="_blank" rel="noopener noreferrer" aria-label="Contact OLS for commercial use">olsme.com/contact</UpdateLink>.
          </UpdateParagraph>
          <UpdateParagraph>
            <strong>Add the OLS Calendar Widget:</strong> Display the OLS Sun Light Calendar day count on your website and support the correct calendar in use. Copy the code below and paste it into your website’s HTML:
          </UpdateParagraph>
          <WidgetCodeWrapper aria-label="OLS Calendar Widget embed code">
            {calendarWidgetCode}
          </WidgetCodeWrapper>
          <UpdateParagraph>
            <strong>Preview the OLS Calendar Widget:</strong>
          </UpdateParagraph>
          <div style={{ textAlign: "center", margin: "20px 0" }}>
            <a href="https://www.olsme.com" target="_blank" rel="noopener noreferrer" aria-label="Visit OLS website">
              <div id="ols-calendar-widget-preview" style={{ display: "inline-block" }}>
                <div style={{
                  width: "6rem",
                  height: "2.5rem",
                  backgroundColor: "#008000",
                  border: "2px solid #00ff00",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "8px",
                  boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                  padding: "5px 10px",
                  color: "#ffffff",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  fontFamily: "Arial, sans-serif"
                }}>
                  Day {Math.floor((new Date().getTime() - new Date("2024-12-22").getTime()) / (1000 * 60 * 60 * 24)) + 1}
                </div>
              </div>
            </a>
          </div>
          <UpdateParagraph>
            <strong>Add the Light Time Clock Widget:</strong> Showcase a Sun-synchronized clock on your website to enhance your connection with natural rhythms. Copy the code below and paste it into your website’s HTML:
          </UpdateParagraph>
          <WidgetCodeWrapper aria-label="Light Time Clock Widget embed code">
            {lightTimeClockWidgetCode}
          </WidgetCodeWrapper>
          <UpdateParagraph>
            <strong>Preview the Light Time Clock Widget:</strong>
          </UpdateParagraph>
          <div style={{ textAlign: "center", margin: "20px 0" }}>
            <a href="https://www.olsme.com" target="_blank" rel="noopener noreferrer" aria-label="Visit OLS website">
              <div id="ols-light-time-clock-preview" style={{ display: "inline-block" }}>
                <div style={{
                  width: "6rem",
                  height: "2.5rem",
                  backgroundColor: "#008000",
                  border: "2px solid #00ff00",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "8px",
                  boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                  padding: "5px 10px",
                  color: "#ffffff",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  fontFamily: "Arial, sans-serif"
                }}>
                  {new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </a>
          </div>
          <UpdateParagraph>
            Don’t miss this radiant opportunity to integrate these widgets into your website and join OLS in promoting a Sun Light Civilization! For more details on how these tools can enhance your meditation practice, visit <UpdateLink href="/OLScalendarTime" aria-label="Learn more about OLS Calendar Time">OLScalendarTime</UpdateLink>. Let’s align with the Sun’s natural rhythms together!
          </UpdateParagraph>
          <UpdateImage
            src="https://via.placeholder.com/800x400?text=OLS+Widgets+Announcement"
            alt="OLS Calendar Widget and Light Time Clock Announcement - Limited-Time Offer"
          />

            <ShareThisWrapper>
            <InlineShareButtons
              config={{
              alignment: "center",
              color: "social",
              enabled: true,
              font_size: 14,
              labels: "cta",
              language: "en",
              networks: [
                "whatsapp",
                "linkedin",
                "messenger",
                "facebook",
                "twitter",
                "blogger",
                "copy_link", // <-- Use "copy_link" instead of "copy"
                "pinterest",
                "sms",
              ],
              padding: 20,
              radius: 3,
            size: 28,
              show_count: true,
              show_faces: true,
              style: "button",
              show_total: true,
              min_count: 10,
              url: "https://www.olsme.com/olsupdates/ols2900/2945",
              image:
                "https://via.placeholder.com/1200x630?text=OLS+Widgets+Announcement",
              description:
                "Limited-time offer (May 17–31, 2025): Add the OLS Calendar Widget to your website and support the correct calendar in use! Plus, check out our new Light Time Clock Widget. Learn more at onelightsystem OLS!",
              title: "Limited-Time Offer: Add OLS Calendar Widget & New Light Time Clock to Your Website! - onelightsystem OLS",
              message:
                "Add the OLS Calendar Widget and new Light Time Clock Widget to your website now (offer ends May 31, 2025)! Support the correct calendar with onelightsystem OLS!",
              subject: "OLS Limited-Time Offer: Add Calendar & Light Time Clock Widgets!",
              username: "onelightsystem",
              }}
            />
            </ShareThisWrapper>
        </UpdateContent>
      </UpdateContainer>
    </Layout>
  );
};

export default OLS2945;
